/**
 * @license
 * Copyright 2026 Clip Team
 * SPDX-License-Identifier: MPL-2.0
 */

import {EventEmitter} from 'events';
import formatMessage from 'format-message';
import {IExtension} from './interfaces/i_extension';
import {AbstractEvent} from './events';
import ExtensionManifest from './interfaces/extension-manifest';

/**
 * Class to manage all of the extensions.
 */
export class ExtensionManager {
    /** Map from ID to all loaded extensions. */
    private loadedExtensions: Map<string, IExtension> = new Map();

    /** Event emitter. */
    private eventEmitter: EventEmitter = new EventEmitter();

    constructor() {}

    /**
     * Load a extension to editor. The method only adds the extension to
     * manager, and the extension will still be disabled until `enableExtension`
     * is called.
     * @throws Will throw an error if extension already loaded.
     * @param extension The extension to load.
     */
    loadExtension(extension: IExtension): void {
        const extensionId = extension.getId();
        if (this.loadedExtensions.has(extensionId)) {
            throw new Error(`Extension with id ${extensionId} already exists.`);
        }

        extension.attachManager(this);
        this.loadedExtensions.set(extensionId, extension);
    }

    /**
     * Check whether the extension has been loaded.
     * @param extensionId Extension ID.
     * @returns True if the extension is loaded.
     */
    isExtensionLoaded(extensionId: string): boolean {
        return this.loadedExtensions.has(extensionId);
    }

    /**
     * Enable the extension with given ID.
     * @throws Will throw an error if extension is not found or has been enabled.
     * @param extensionId ID of the extension to enable.
     */
    enableExtension(extensionId: string): Promise<void> {
        const extension = this.getExtensionById(extensionId);
        if (!extension) {
            throw new Error(`Extension ${extensionId} is not found.`);
        }

        if (extension.isEnabled()) {
            throw new Error(`Extension ${extensionId} is already enabled.`);
        }

        return extension.enable();
    }

    /**
     * Disable the extension with given ID.
     * @throws Will throw an error if extension is not found or not enabled.
     * @param extensionId ID of the extension to disable.
     */
    disableExtension(extensionId: string): Promise<void> {
        const extension = this.getExtensionById(extensionId);
        if (!extension) {
            throw new Error(`Extension ${extensionId} is not found.`);
        }

        if (!extension.isEnabled()) {
            throw new Error(`Extension ${extensionId} is not enabled.`);
        }

        return extension.disable();
    }

    /**
     * Check whether the extension is enabled.
     * @param extensionId ID of the extension to check.
     * @returns True if the extension is enabled.
     */
    isExtensionEnabled(extensionId: string): boolean {
        const extension = this.getExtensionById(extensionId);
        return !!extension && extension.isEnabled();
    }

    /**
     * Get the extension object.
     * @param extensionId ID of the extension.
     * @returns The extension object.
     */
    private getExtensionById(extensionId: string): IExtension | null {
        return this.loadedExtensions.get(extensionId) ?? null;
    }

    /**
     * Get toolbox contents for Blockly.
     * @param isStage True if current target is stage.
     * @returns Toolbox contents.
     */
    getToolboxContents(isStage: boolean): any {
        const toolboxContents = [];
        for (const extension of this.loadedExtensions.values()) {
            if (!extension.isEnabled()) continue;
            toolboxContents.push(extension.getToolboxContents(isStage));
        }
        return toolboxContents;
    }

    /**
     * Get data needed to list all extensions in extension library.
     * @returns Manifest of all extensions.
     */
    getManifest(): ExtensionManifest[] {
        const result: ExtensionManifest[] = [];
        this.loadedExtensions.forEach((extension) => {
            result.push(extension.getManifest());
        });
        return result;
    }

    /**
     * Regenerate blockinfo for any enabled extensions.
     */
    async refreshInfo(): Promise<void[]> {
        const promises: Promise<void>[] = [];
        this.loadedExtensions.forEach((extension) => {
            if (extension.isEnabled()) {
                promises.push(extension.refreshInfo());
            }
        });
        return Promise.all(promises);
    }

    /**
     * Add an event listener.
     * @param event Name of the event.
     * @param listener Callback function.
     */
    addEventListener(event: string, listener: (...args: any[]) => void): void {
        this.eventEmitter.addListener(event, listener);
    }

    /**
     * Remove an event listener.
     * @param event Name of the event.
     * @param listener Callback function.
     */
    removeEventListener(event: string, listener: (...args: any[]) => void): void {
        this.eventEmitter.removeListener(event, listener);
    }

    /**
     * Emit a event.
     * @param event Payload of event.
     */
    emitEvent<T extends AbstractEvent>(event: T): void {
        this.eventEmitter.emit(event.type, event);
    }

    /**
     * Set the current locale and builtin messages for the VM.
     * @param locale Current locale.
     * @param messages Builtin messages map for current locale.
     * @returns Promise that resolves when all the blocks have been updated for a new locale.
     *      (or empty if locale hasn't changed).
     */
    setLocale(locale: string, messages: Record<string, string | formatMessage.Translation>) {
        if (locale !== formatMessage.setup().locale) {
            formatMessage.setup({locale, translations: {[locale]: messages}});
        }
        return this.refreshInfo();
    }

    /**
     * Get the current locale for the VM.
     * @returns The current locale in the VM.
     */
    getLocale(): formatMessage.Locales {
        return formatMessage.setup().locale;
    }
}
